import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Icon } from "@/components/Icons";
import { requestSignalResearch } from "../../research-assistant-api";

/**
 * A signal-grounded research assistant at the foot of the internal detail
 * column. Answers come from the authenticated Edge Function and are bounded to
 * the signal, its practice/relationship context, and its retained source link.
 *
 * Dismissal is preserved from the prior build: an outside press (document
 * capture-phase pointerdown, covering mouse/touch/pen) and a capture-phase
 * Escape that closes this one layer without falling through to the modal's own
 * Escape-to-close.
 */
export function ResearchAssistant({
  signalId,
  company,
}: {
  signalId: string;
  company: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const research = useMutation({
    mutationFn: (nextQuestion: string) =>
      requestSignalResearch({ signalId, question: nextQuestion }),
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div className="sx-research" ref={rootRef}>
      <button
        type="button"
        className="sx-strip"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="sx-s-label">
          <Icon name="sparkles" />
          Research Assistant
        </span>
        <span
          className={`sx-strip-chevron${open ? " sx-strip-chevron--open" : ""}`}
          aria-hidden="true"
        >
          <Icon name="chevron-down" />
        </span>
      </button>
      {open ? (
        <div className="sx-research-body">
          <p>
            Ask about {company} or this opportunity. Answers are grounded in the signal, its BDSE
            context, and the retained source evidence.
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const nextQuestion = question.trim();
              if (nextQuestion) research.mutate(nextQuestion);
            }}
          >
            <textarea
              className="sx-field"
              placeholder={`Ask about ${company} or this signal…`}
              value={question}
              maxLength={1000}
              onChange={(event) => setQuestion(event.target.value)}
              aria-label="Research assistant question"
            />
            <div className="sx-research-foot">
              <span className="sx-research-note">Signal-grounded · source cited</span>
              <button
                type="submit"
                className="sx-cta"
                disabled={!question.trim() || research.isPending}
              >
                {research.isPending ? "Researching…" : "Ask"}
              </button>
            </div>
          </form>
          {research.isError ? (
            <p className="sx-research-error" role="alert">
              {research.error.message || "The assistant could not answer right now."}
            </p>
          ) : null}
          {research.data ? (
            <div className="sx-research-answer" aria-live="polite">
              <p>{research.data.answer}</p>
              {research.data.citations.length > 0 ? (
                <div className="sx-research-citations">
                  <span>Grounded in</span>
                  {research.data.citations.map((citation) =>
                    citation.url ? (
                      <a key={citation.id} href={citation.url} target="_blank" rel="noreferrer">
                        [{citation.id}] {citation.label}
                      </a>
                    ) : (
                      <span key={citation.id}>
                        [{citation.id}] {citation.label}
                      </span>
                    )
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
